import { useGet, usePatch, usePost } from "@/apiHooks/HttpFunctions";
import {
  blogCategoriesApi,
  blogPostsApi,
  sportsMasterDataApi,
} from "@/apis/apis";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import Button from "@/components/Button";
import ImageInput from "@/components/ImageInput/Index";
import PageHeaderName from "@/components/PageHeaderName/PageHeaderName";
import RenderToast from "@/components/RenderToast";
import RichTextEditor from "@/components/RichTextEditor";
import Spinner from "@/components/Spinner";
import { blogPostHistoryActionLabels } from "@/constant/Blogs/Posts/history-action";
import { LOADING_PLACEHOLDER } from "@/constant/constants";
import { HTTP_METHODS } from "@/constant/Http/methods";
import { Active, Deactivated } from "@/constant/imagePath";
import {
  DEFAULT_LANGUAGE,
  DEFAULT_LANGUAGE_LABEL,
} from "@/constant/Languages/language";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import {
  STATUS,
  statusBgColors,
  statusLabels,
} from "@/constant/statusConstants";
import { useLayout } from "@/context/LayoutContext";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import { useModalInfo } from "@/context/ModalContext";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import getPostInitialValues from "@/data/BlogManagement/Posts/initial-values";
import useUnsavedChangesWarning from "@/hooks/useUnsavedChangesWarning";
import getPostSchema from "@/schema/BlogManagement/Posts/schema";
import { cn } from "@/utils/cn";
import { formatDateTime } from "@/utils/helper";
import { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useFormik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

export default function BlogPostsFormPage() {
  // Path Params
  const { id } = useParams();
  const isEdit = !!id;

  // Hooks
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const targetLang = searchParams.get("target_lang") || DEFAULT_LANGUAGE;
  const { setIsFullPage } = useLayout();
  const { openModalConfirmation } = useModalConfirmation();
  const { openModalInfo } = useModalInfo();
  const navigate = useNavigate();
  const initCurrentCount = { char: 0, word: 0 };
  const [currentCount, setCurrentCount] = useState(initCurrentCount);
  const showApi = isEdit ? `${blogPostsApi}/${id}` : null;
  const {
    data: showData,
    isFetching: showIsFetching,
    error: showError,
  } = useGet({
    apiURL: showApi,
    params: { target_lang: targetLang },
    enabled: isEdit,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
  const {
    data: showMetaData,
    isFetching: showMetaIsFetching,
    error: showMetaError,
  } = useGet({
    apiURL: `${showApi}/meta`,
    enabled: isEdit,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getPostInitialValues(
      isEdit && !showIsFetching ? showData : null
    ),
    validationSchema: getPostSchema(isEdit, targetLang),
    onSubmit: (values) => handleSubmit(values),
  });
  useUnsavedChangesWarning(formik.dirty);
  const statusLabelsMap = statusLabels();
  const statusRadioOptions = [
    {
      value: STATUS.ACTIVE,
      label: statusLabelsMap[STATUS.ACTIVE],
      imageSrc: Active,
    },
    {
      value: STATUS.INACTIVE,
      label: statusLabelsMap[STATUS.INACTIVE],
      imageSrc: Deactivated,
    },
  ];

  // Update the current counts
  const updateCounts = useCallback((text) => {
    setCurrentCount({
      char: countChar(text),
      word: countWord(text),
    });
  }, []);

  // useEffects
  useEffect(() => {
    if (showError?.status === 404) {
      RenderToast({ type: "error", message: "Post translation not found." });
    }
    if (showMetaError?.status === 404) {
      RenderToast({
        type: "error",
        message: "Failed to get all of the post translation status.",
      });
    }
  }, [showError, showMetaError]);
  useEffect(() => {
    if (targetLang) {
      const params = new URLSearchParams();
      params.set("target_lang", targetLang);
      setSearchParams(params);
    }
  }, [targetLang, setSearchParams]);
  useEffect(() => {
    setIsFullPage(false);
    return () => setIsFullPage(true);
  }, [setIsFullPage]);

  // Functions
  const triggerUnsavedChangesWarning = useUnsavedChangesWarning(formik.dirty);
  const handleLanguageTab = (event, localeCode) => {
    if (!triggerUnsavedChangesWarning()) {
      event.preventDefault();
      return;
    }

    formik.resetForm();

    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("target_lang", localeCode);
      return newParams;
    });
  };
  const getTextFromHtml = (html) => {
    const editor = new Editor({
      extensions: [StarterKit],
      content: html,
    });
    return editor.getText();
  };
  const handleSubmit = (values) => {
    const formData = new FormData();

    if (targetLang === DEFAULT_LANGUAGE) {
      if (isEdit) formData.append("_method", "PUT");
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][title]`,
        values.title
      );
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][sub_title]`,
        values.sub_title
      );
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][content]`,
        values.content
      );
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][status]`,
        values.status
      );
      formData.append("sport_id", values.sport_id);
      formData.append("blog_category_id", values.blog_category_id);
      formData.append("auto_translate", values.auto_translate);
      if (values.thumbnail_url && values.thumbnail_url instanceof File)
        formData.append("thumbnail_url", values.thumbnail_url);
      if (values.banner_url && values.banner_url instanceof File)
        formData.append("banner_url", values.banner_url);

      save(formData);
      return;
    }

    formData.append("target_lang", targetLang);
    formData.append("title", values.title);
    formData.append("sub_title", values.sub_title);
    formData.append("content", values.content);
    formData.append("status", values.status);
    saveTranslation(formData);
  };
  const handleTranslate = () => {
    openModalConfirmation({
      title: "It will overwrite any existing text.",
      customMutation: {
        confirmationMessage: "Are you sure you want to translate the text?",
        apiURL: `${blogPostsApi}/translate/${id}?target_lang=${targetLang}`,
        action: HTTP_METHODS.PUT,
        onSuccess: () => {
          openModalInfo("Post translated successfully.");
        },
        onError: (error) => {
          RenderToast({
            type: "error",
            message:
              error.response.data.errors.error ?? "Something went wrong.",
          });
        },
      },
    });
  };
  const handleSuccess = (action) => (data) => {
    RenderToast({
      type: "success",
      message: `Post ${action} successfully.`,
    });
    if (!isEdit) navigate("/blog-management/blog-posts");
  };
  const countChar = (text = "") => text.replace(/[\r\n]/g, "").length;
  const countWord = (text = "") =>
    text.trim().split(/\s+/).filter(Boolean).length;

  // Init the current content counts
  useEffect(() => {
    const content = getTextFromHtml(showData?.data?.content);
    updateCounts(content);
  }, [showData?.data?.content, updateCounts]);

  // Save form
  const {
    mutate: save,
    isPending: saveIsPending,
    error: saveError,
  } = usePost({
    isFormData: true,
    apiURL: isEdit ? `${blogPostsApi}/${id}` : blogPostsApi,
    onSuccess: handleSuccess(isEdit ? "edited" : "added"),
  });
  const {
    mutate: saveTranslation,
    isPending: saveTranslationIsPending,
    error: saveTranslationError,
  } = usePatch({
    apiURL: `${blogPostsApi}/translate-overwritten/${id}`,
    onSuccess: handleSuccess("edited"),
  });

  // Get dropdown data
  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[status]=${STATUS.ACTIVE}&filter[keyword]=`;
  const sportListApi = `${sportsMasterDataApi}${baseQueryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
    getSelectedOption: sportGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.sport,
  });
  const blogCategoryListApi = `${blogCategoriesApi}${baseQueryParams}`;
  const {
    loading: blogCategoryLoading,
    defaultOptions: blogCategoryDefaultOptions,
    debouncedFetchOptions: blogCategoryFetchOptions,
    getSelectedOption: blogCategoryGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: blogCategoryListApi,
    formikValue: formik.values.blog_category_id,
    formikInitialValue: formik.initialValues.blog_category_id,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.category,
  });

  const statusPerLanguage = showMetaData?.data?.status_per_language;
  const defaultLanguage = showMetaData?.data?.default_language;
  const content = getTextFromHtml(defaultLanguage?.content);
  const dlContentCharCount = countChar(content);
  const dlContentWordCount = countWord(content);

  // Meta data
  const Meta = () => {
    const meta = showData?.data?.meta;

    const currentState = meta?.history;
    const lastOverwrite = meta?.last_overwrite;
    const lastTranslation = meta?.last_translation;

    const currentCharCount = currentCount?.char || 0;
    const currentWordCount = currentCount?.word || 0;

    return (
      <Row className="flex items-end border-b-2 border-gray-100 pb-2">
        <Col md={12}>
          <div className="text-jersium-black font-extralight text-sm grid grid-cols-2">
            <div>
              {/* Current state action */}
              {currentState &&
                (targetLang === DEFAULT_LANGUAGE ? (
                  <div className="text-red-700">
                    Original, used for Translation
                  </div>
                ) : (
                  <div>
                    {blogPostHistoryActionLabels[currentState.action] ?? null}{" "}
                    by {currentState.by}
                  </div>
                ))}

              {/* Last overwrite */}
              <div>
                {targetLang === DEFAULT_LANGUAGE
                  ? "Last Change: "
                  : "Last Overwrite: "}

                {lastOverwrite
                  ? `${formatDateTime(lastOverwrite.date)} by ${
                      lastOverwrite.by
                    }`
                  : "-"}
              </div>

              {/* Last translation */}
              <div>
                {targetLang !== DEFAULT_LANGUAGE &&
                  `Last Translation: ${
                    lastTranslation ? formatDateTime(lastTranslation.date) : "-"
                  }`}
              </div>
            </div>

            <div className="pl-3">
              {/* Counts */}
              <div>
                Character Count ({DEFAULT_LANGUAGE_LABEL} / Current):{" "}
                {!showMetaData || showMetaIsFetching ? "-" : dlContentCharCount}{" "}
                / {currentCharCount}
              </div>
              <div>
                Word Count ({DEFAULT_LANGUAGE_LABEL} / Current):{" "}
                {!showMetaData || showMetaIsFetching ? "-" : dlContentWordCount}{" "}
                / {currentWordCount}
              </div>

              {/* # times translated */}
              <div>
                {targetLang !== DEFAULT_LANGUAGE &&
                  `# Times Translated: ${
                    !meta || showIsFetching
                      ? "-"
                      : currentState.translated_count
                  }`}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  // Default language forms
  const defaultLangForms = targetLang === DEFAULT_LANGUAGE && (
    <>
      <Row>
        <Col md={6}>
          <AsyncDropdown
            height={32}
            label="Sport ID"
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !mx-0"
            placeholder="No sports selected. Type to search..."
            defaultOptions={sportDefaultOptions}
            loadOptions={sportFetchOptions}
            isLoading={sportLoading}
            value={sportGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("sport_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
        <Col md={6}>
          <AsyncDropdown
            height={32}
            label="Category"
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            placeholder="No categories selected. Type to search..."
            defaultOptions={blogCategoryDefaultOptions}
            loadOptions={blogCategoryFetchOptions}
            isLoading={blogCategoryLoading}
            value={blogCategoryGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("blog_category_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.blog_category_id &&
                formik.errors.blog_category_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.blog_category_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <ImageInput
            id="thumbnail_url"
            formFormik={formik}
            fieldName="thumbnail_url"
            label="Thumbnail"
            labelClass="mb-0 flex items-center"
            error={saveError}
            existingImageUrl={isEdit && showData?.data?.thumbnail_url}
            clickablePreview={true}
            customStyle={{ width: "100%" }}
            imagePreviewWrapper={"col-span-5"}
            className={"grid grid-cols-6"}
            inputContainerClass={"col-span-5 border-0"}
            imagePreviewContainer={"grid grid-cols-6 gap-3"}
            errorTextClass={"ml-0"}
          />
        </Col>
        <Col md={6}>
          <ImageInput
            id="banner_url"
            formFormik={formik}
            fieldName="banner_url"
            label="Banner"
            labelClass="mb-0 flex items-center"
            error={saveError}
            existingImageUrl={isEdit && showData?.data?.banner_url}
            clickablePreview={true}
            customStyle={{ width: "100%" }}
            imagePreviewWrapper={"col-span-5"}
            className={"grid grid-cols-6"}
            inputContainerClass={"col-span-5 border-0"}
            imagePreviewContainer={"grid grid-cols-6 gap-3"}
            errorTextClass={"ml-0"}
          />
        </Col>
      </Row>
    </>
  );

  const isPending = saveIsPending || saveTranslationIsPending;
  const error = saveError || saveTranslationError;

  // Error handlers
  useFormikErrorToast(formik);
  useHttpErrorToast(error);

  return (
    <>
      <div className="-mt-1.5">
        <PageHeaderName title={isEdit ? "Edit Post" : "Add Post"} />
      </div>

      <ul className="flex flex-grow text-sm font-medium text-center text-white border-b-2 border-gray-100 mt-2 mb-2.5 w-full">
        {locales.map((locale) => (
          <li key={locale.code}>
            <button
              type="button"
              aria-current="page"
              className={cn(
                "flex gap-x-2 items-center px-3 py-2 rounded-t-lg text-nowrap disabled:cursor-not-allowed",
                targetLang === locale.code
                  ? "text-jersium-black bg-gray-100"
                  : "text-gray-500 disabled:text-gray-300 enabled:hover:bg-gray-100"
              )}
              onClick={(e) => handleLanguageTab(e, locale.code)}
              disabled={!id && locale.code !== DEFAULT_LANGUAGE}
            >
              {/* Post status circle indicator */}
              {!isEdit && locale.code !== DEFAULT_LANGUAGE ? (
                <span className="inline-block w-2 h-2 rounded-full bg-gray-100" />
              ) : (
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{
                    background:
                      statusBgColors[
                        statusPerLanguage?.[locale.code] || STATUS.INACTIVE
                      ],
                  }}
                />
              )}

              {/* The locale */}
              <span>
                {locale.code.toUpperCase()}
                {targetLang === locale.code && formik.dirty ? "*" : " "}
              </span>

              {/* Loading spinner */}
              {targetLang === locale.code && showIsFetching && (
                <Spinner color="gray" />
              )}
            </button>
          </li>
        ))}
      </ul>

      {!(isEdit && showIsFetching) && (
        <form
          onSubmit={formik ? formik.handleSubmit : null}
          className="overflow-hidden flex gap-y-2 flex-col justify-center items-center w-full"
        >
          <div className="flex-1 flex flex-col gap-y-2 w-full">
            <Meta />
            <Row className="border-b-2 border-gray-100 pb-2 mb-2">
              <Col md={12}>
                <div className="mt-2 w-full flex justify-between">
                  <div className="gap-x-6 flex justify-start">
                    <div className="flex gap-x-2 items-center">
                      <label className="block text-sm me-4">
                        Language Status
                      </label>
                      {statusRadioOptions.map((option) => (
                        <label
                          key={option.value}
                          className="flex gap-2 items-center"
                        >
                          <input
                            id={`status-${option.value}`}
                            type="radio"
                            value={option.value}
                            checked={formik.values.status === option.value}
                            onChange={() =>
                              formik.setFieldValue("status", option.value)
                            }
                          />
                          <div
                            className={cn(
                              "flex items-center gap-2 px-3 py-1 h-8 rounded-md",
                              option.value === STATUS.ACTIVE
                                ? "active-status"
                                : "deActive-status"
                            )}
                          >
                            <span className="text-sm font-extralight">
                              {option.label}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="gap-x-6 flex justify-start">
                    <div className="flex justify-end items-center gap-2.5 h-9">
                      {targetLang !== DEFAULT_LANGUAGE ? (
                        <Button
                          variant="green-gradient"
                          label={`${
                            isPending ? LOADING_PLACEHOLDER : "Translate"
                          }`}
                          onClick={handleTranslate}
                          type="button"
                          disabled={isPending}
                          className="!w-[200px] !text-[14px]"
                        />
                      ) : (
                        <>
                          <div className="flex my-auto">
                            <input
                              id="auto_translate"
                              type="checkbox"
                              value={formik.values.auto_translate}
                              checked={formik.values.auto_translate}
                              className={`h-3 w-3 border rounded-md ${
                                formik.touched.auto_translate &&
                                formik.errors.auto_translate
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                              onChange={(event) =>
                                formik.setFieldValue(
                                  "auto_translate",
                                  event.target.checked
                                )
                              }
                            />
                          </div>
                          <label
                            htmlFor="auto_translate"
                            className="block text-sm"
                          >
                            Translate All
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="gap-x-6 flex justify-start">
                    <div>
                      <Button
                        variant="outlined"
                        label="Cancel/Back"
                        onClick={() => navigate("/blog-management/blog-posts")}
                        type="button"
                        disabled={isPending}
                        className="!w-[200px] !text-[14px]"
                      />
                    </div>
                    <div>
                      <Button
                        variant="green-gradient"
                        label={`${isPending ? LOADING_PLACEHOLDER : "Save"}`}
                        type="submit"
                        disabled={isPending}
                        className="!w-[200px] !text-[14px]"
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <BasicInput
                  formik={formik}
                  fieldName={"title"}
                  saveError={saveError}
                  label="Title"
                  labelClass="mb-0 flex items-center"
                  inputDivClass={"grid grid-cols-6"}
                  inputClass={"col-span-5"}
                  errorTextClass={"block"}
                />
              </Col>
              <Col md={6}>
                <BasicInput
                  formik={formik}
                  fieldName={"sub_title"}
                  saveError={saveError}
                  label="Subtitle"
                  labelClass="mb-0 flex items-center"
                  inputDivClass={"grid grid-cols-6"}
                  inputClass={"col-span-5"}
                  errorTextClass={"block"}
                />
              </Col>
            </Row>
            {targetLang === DEFAULT_LANGUAGE && defaultLangForms}
            <Row>
              <Col md={12}>
                <RichTextEditor
                  formik={formik}
                  fieldName={"content"}
                  saveError={saveError}
                  label={""}
                  enableCharCounter={false}
                  enableWordCounter={false}
                  onChange={(editor) => {
                    const editorText = editor.getText();
                    updateCounts(editorText);
                    const newContent = editor.getHTML();
                    formik.setFieldValue("content", newContent);
                  }}
                />
              </Col>
            </Row>
          </div>
        </form>
      )}
    </>
  );
}
